/**
 * GenetecPlayerTest.jsx
 * =====================
 * Standalone test page for the Genetec Web Player.
 * Navigate to /genetec-test in the browser.
 * Fetches stream config from the API and renders the GWP player.
 */

import { useState, useEffect } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import GenetecPlayer from '../components/GenetecPlayer';
import { Video, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const GenetecPlayerTest = () => {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [streamConfig, setStreamConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerError, setPlayerError] = useState(null);

  useEffect(() => {
    loadCameras();
  }, []);

  const loadCameras = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithTenant('/api/vms/cameras').then(r => r.json());
      const genetecCams = (res.cameras || []).filter(c => c.integrationType === 'GENETEC_VMS');
      setCameras(genetecCams);
      if (genetecCams.length > 0) {
        selectCamera(genetecCams[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectCamera = async (camera) => {
    setSelectedCamera(camera);
    setStreamConfig(null);
    setPlayerError(null);
    try {
      const config = await fetchWithTenant(
        `/api/vms/genetec/${camera.integrationId}/stream-config/${encodeURIComponent(camera.id)}`
      ).then(r => r.json());
      console.log('[GenetecTest] Stream config:', config);
      setStreamConfig(config);
    } catch (err) {
      setError('Failed to get stream config: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading Genetec cameras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="md-page" style={{ padding: '24px' }}>
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title"><Video size={22} /> Genetec Player Test</h1>
          <p className="page-subtitle">
            {cameras.length} Genetec camera{cameras.length !== 1 ? 's' : ''} found
          </p>
        </div>
        <button className="btn btn-secondary" onClick={loadCameras}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
          marginBottom: '16px',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {cameras.length === 0 && !error && (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <Video size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <h3>No Genetec Cameras</h3>
          <p style={{ color: 'var(--text-secondary)' }}>Add cameras to your Genetec Security Center and they will appear here.</p>
        </div>
      )}

      {cameras.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '16px' }}>
          {/* Camera list */}
          <div className="card" style={{ padding: '12px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>Cameras</h4>
            {cameras.map(cam => (
              <button
                key={cam.id}
                onClick={() => selectCamera(cam)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: '4px',
                  background: selectedCamera?.id === cam.id ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  border: selectedCamera?.id === cam.id ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <div style={{ fontWeight: 500 }}>{cam.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {cam.status === 'online' ? (
                    <span style={{ color: '#10b981' }}><CheckCircle size={10} style={{ marginRight: '4px' }} />Online</span>
                  ) : (
                    <span style={{ color: '#ef4444' }}><XCircle size={10} style={{ marginRight: '4px' }} />Offline</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Player area */}
          <div>
            {selectedCamera && (
              <div className="card" style={{ padding: '16px' }}>
                <h3 style={{ margin: '0 0 8px 0' }}>{selectedCamera.name}</h3>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  GUID: {selectedCamera.id}
                </div>

                {/* Debug info */}
                {streamConfig && (
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                    background: 'rgba(255,255,255,0.03)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}>
                    <div>Media GW: {streamConfig.mediaGatewayEndpoint}</div>
                    <div>Token: {streamConfig.tokenEndpoint}</div>
                    <div>gwp.js: {streamConfig.gwpJsUrl}</div>
                  </div>
                )}

                {/* Player */}
                {streamConfig ? (
                  <div style={{ width: '100%', aspectRatio: '16/9', minHeight: '360px' }}>
                    <GenetecPlayer
                      cameraGuid={streamConfig.cameraGuid}
                      integrationId={streamConfig.integrationId}
                      mediaGatewayEndpoint={streamConfig.mediaGatewayEndpoint}
                      gwpJsUrl={streamConfig.gwpJsUrl}
                      tokenEndpoint={streamConfig.tokenEndpoint}
                      autoPlay={true}
                      onError={(msg) => setPlayerError(msg)}
                      onPlayerReady={(p) => console.log('[GenetecTest] Player ready', p)}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
                    Loading stream config...
                  </div>
                )}

                {playerError && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '4px',
                    color: '#ef4444',
                    fontSize: '12px',
                  }}>
                    Player error: {playerError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenetecPlayerTest;
